import { useEffect, type ReactNode } from 'react';
import { useAuth, redirectToLogin } from '../hooks/useAuth';

const AUTH_DISABLED = import.meta.env.VITE_AUTH_DISABLED === 'true';

/**
 * Wraps the app with auth check.
 * - Loading: shows spinner
 * - Unauthenticated/expired: redirects to portal login
 * - Forbidden (no guadalajara access): shows message
 * - Error (portal unreachable): shows retry
 * - Authenticated: renders children
 *
 * Set VITE_AUTH_DISABLED=true in .env to bypass in development.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { status, error, fetchMe } = useAuth();

  useEffect(() => {
    if (AUTH_DISABLED) return;
    fetchMe();
  }, [fetchMe]);

  if (AUTH_DISABLED) return <>{children}</>;

  if (status === 'loading') {
    return (
      <div className="h-screen bg-gray-950 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
          <span className="text-sm text-gray-400 font-mono">Verificando sesión…</span>
        </div>
      </div>
    );
  }

  if (status === 'unauthenticated') {
    redirectToLogin();
    return (
      <div className="h-screen bg-gray-950 flex items-center justify-center">
        <span className="text-sm text-gray-400 font-mono">Redirigiendo al portal…</span>
      </div>
    );
  }

  if (status === 'forbidden') {
    return (
      <div className="h-screen bg-gray-950 flex items-center justify-center">
        <div className="text-center max-w-md px-6">
          <div className="text-4xl mb-4">🔒</div>
          <h2 className="text-lg font-semibold text-white mb-2">Sin acceso</h2>
          <p className="text-sm text-gray-400 mb-4">
            Tu cuenta no tiene acceso a la aplicación de Guadalajara.
            Contacta al administrador de tu organización.
          </p>
          <button
            onClick={() => { window.location.href = 'https://app.ars93.com'; }}
            className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg text-sm transition-colors"
          >
            Volver al portal
          </button>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="h-screen bg-gray-950 flex items-center justify-center">
        <div className="text-center max-w-md px-6">
          <div className="text-4xl mb-4">⚠️</div>
          <h2 className="text-lg font-semibold text-white mb-2">Error de autenticación</h2>
          <p className="text-sm text-gray-400 mb-4">
            {error === 'network'
              ? 'No se pudo conectar con el servidor.'
              : `Error: ${error}`}
          </p>
          <button
            onClick={() => fetchMe()}
            className="px-4 py-2 bg-cyan-800 hover:bg-cyan-700 text-white rounded-lg text-sm transition-colors"
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
